import React from "react";
import { App as AntdApp, Drawer, Segmented, List, Tag, Tooltip, Empty, Spin, Typography } from "antd";
import { FileImageOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

import api from "@/api/axios.js";
import { getPromoSerials } from "../api/promoApi.js";
import "./SerialsDrawer.css";

const { Text } = Typography;

const TAB_ALL = "all";
const TAB_DUP = "dup";

// Risolve l'URL assoluto delle immagini /media/... (pattern usato in FlyerBannerSection).
const resolveImageUrl = (pathValue) => {
	if (!pathValue) return "";
	const trimmed = String(pathValue).trim();
	if (!trimmed) return "";
	if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("data:")) return trimmed;
	try {
		const base = api?.defaults?.baseURL || window.location.origin;
		return new URL(trimmed, base).href;
	}
	catch {
		return trimmed;
	}
};

function Thumb({ url, alt }) {
	const src = resolveImageUrl(url);
	if (!src)
		return <div className="serials-thumb serials-thumb--empty"><FileImageOutlined /></div>;
	return <img className="serials-thumb" src={src} alt={alt || ""} loading="lazy" draggable="false" />;
}

const sameText = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

// Comprime i seriali mancanti in intervalli (1, 2, 3...8 → #1–8) invece di elencarli uno per uno:
// con volantini grandi i buchi possono essere decine, un elenco piatto sarebbe illeggibile.
// `missing` arriva già ordinato crescente (costruito iterando 1..maxSerial in promo.controller.js).
function formatMissingRanges(missing) {
	if (!missing.length) return "";
	const ranges = [];
	let start = missing[0];
	let prev = missing[0];
	for (let i = 1; i <= missing.length; i++) {
		const n = missing[i];
		if (n === prev + 1) { prev = n; continue; }
		ranges.push(start === prev ? `#${start}` : `#${start}–${prev}`);
		if (n !== undefined) { start = n; prev = n; }
	}
	return ranges.join(", ");
}

// Riga di riferimento pagina/volantino: cliccabile quando sappiamo dove atterrare (serve
// flyerId, la pagina è opzionale — se assente si apre comunque il volantino sulla prima pagina).
function SerialRefLink({ flyerId, flyerName, page, isVariant }) {
	const label = <>{flyerName || "—"}{isVariant ? " · variante" : ""}{page != null ? ` · pag. ${page}` : ""}</>;
	if (!flyerId)
		return <Text type="secondary" className="serials-sub">{label}</Text>;
	return (
		<Link className="serials-sub serials-ref-link" to={`/flyermanage/${flyerId}${page != null ? `/${page}` : ""}`}>
			{label}
		</Link>
	);
}

function SerialRow({ item }) {
	const showName = item.nome_prodotto && !sameText(item.nome_prodotto, item.descrizione);
	return (
		<div className={`serials-row${item.conflict ? " serials-row--conflict" : ""}`}>
			<Tooltip title={item.conflict ? "Stesso numero assegnato a prodotti diversi" : undefined}>
				<Tag className="serials-num" color={item.conflict ? "error" : undefined}>#{item.seriale}</Tag>
			</Tooltip>
			<Thumb url={item.photoUrl} alt={item.descrizione} />
			<div className="serials-info">
				{item.descrizione
					? <Text className="serials-desc">{item.descrizione}</Text>
					: <Text type="secondary" className="serials-desc">(senza descrizione)</Text>}
				<SerialRefLink flyerId={item.flyerId} flyerName={item.flyerName} page={item.page} isVariant={item.isVariant} />
				{showName && <Text type="secondary" className="serials-sub">{item.nome_prodotto}</Text>}
			</div>
		</div>
	);
}

export default function SerialsDrawer({ open, promoId, promoName, onClose }) {
	const { message } = AntdApp.useApp();
	const [loading, setLoading] = React.useState(false);
	const [data, setData] = React.useState(null);
	const [tab, setTab] = React.useState(TAB_ALL);

	React.useEffect(() => {
		if (!open || !promoId) return undefined;
		let alive = true;
		setLoading(true);
		setTab(TAB_ALL);
		getPromoSerials(promoId)
			.then((res) => { if (alive) setData(res); })
			.catch((error) => {
				if (!alive) return;
				setData(null);
				message.error(error?.response?.data?.error || "Impossibile caricare i seriali");
			})
			.finally(() => { if (alive) setLoading(false); });
		return () => { alive = false; };
	}, [open, promoId, message]);

	const serials = data?.serials || [];
	const duplicateGroups = data?.duplicateGroups || [];
	const stats = data?.stats || {};
	const missingSerials = data?.missingSerials || [];
	const gaps = stats.gaps || 0;
	const duplicateSerials = stats.duplicateSerials || 0;

	return (
		<Drawer
			title={`Seriali — ${promoName || "Promo"}`}
			placement="right"
			width={520}
			open={open}
			onClose={onClose}
			className="serials-drawer"
		>
			<Spin spinning={loading}>
				<div className="serials-badges">
					<Tooltip title={missingSerials.length ? `Mancanti: ${formatMissingRanges(missingSerials)}` : "Nessun buco nella sequenza"}>
						<Tag color={gaps > 0 ? "warning" : "default"}>Buchi: {gaps}</Tag>
					</Tooltip>
					<Tooltip title={duplicateSerials > 0 ? "Stesso numero di seriale con prodotti diversi (anomalia)" : "Nessun seriale doppio"}>
						<Tag color={duplicateSerials > 0 ? "error" : "default"}>Seriali doppi: {duplicateSerials}</Tag>
					</Tooltip>
				</div>

				<Segmented
					block
					value={tab}
					onChange={setTab}
					options={[
						{ label: `Tutti i seriali (${serials.length})`, value: TAB_ALL },
						{ label: `Duplicati prodotto (${duplicateGroups.length})`, value: TAB_DUP }
					]}
					style={{ marginBottom: 12 }}
				/>

				{tab === TAB_ALL && (
					serials.length
						? (
							<List
								className="serials-list"
								dataSource={serials}
								rowKey={(it) => it.cardId || `${it.seriale}-${it.flyerId}`}
								renderItem={(it) => (
									<List.Item>
										<SerialRow item={it} />
									</List.Item>
								)}
							/>
						)
						: (!loading && <Empty description="Nessun seriale" />)
				)}

				{tab === TAB_DUP && (
					duplicateGroups.length
						? (
							<div className="serials-dups">
								{duplicateGroups.map((group, gi) => (
									<div className="serials-dup-group" key={`${group.descrizione}-${gi}`}>
										<div className="serials-dup-head">
											<Text strong className="serials-desc">{group.descrizione || "(senza descrizione)"}</Text>
											<Tag color="error">{group.items.length} seriali</Tag>
										</div>
										{group.items.map((it) => (
											<SerialRow key={it.cardId || `${gi}-${it.seriale}`} item={it} />
										))}
									</div>
								))}
							</div>
						)
						: (!loading && <Empty description="Nessun duplicato" />)
				)}
			</Spin>
		</Drawer>
	);
}
